// Capture command room — the Pursuit shaping home.
//
// Routed at /capture/:id. Surfaces the pursuit with Overview (hypothesis +
// go/no-go + GovCon graph), RFIs, Interactions, and Workstreams. The three
// child tabs render as sortable/filterable data tables; a row click opens a
// side Sheet. All writes round-trip through zou.
//
// No mock data — if the workspace query 404s the page shows an error state.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { ProjectDataTypes } from '@mono/proto-clients';
import { Skeleton, SpecraBadge, SpecraButton, SpecraTabBar, SpecraTabPanel } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, SearchX, Unlink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConvertToProposalDialog } from '../../components/Capture/ConvertToProposalDialog';
import { LifecycleNodeKind, LinkedChips } from '../../components/LinkedChips';
import { PageBody } from '../../components/layout';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { EntityTable } from '../Delivery/_shared/components/EntityTable';
import { EmptyState, ErrorState } from '../Delivery/_shared/components/PageStates';
import { useCreateRfiResponseProject, useListRfiResponsesForCapture, useUnlinkRfiResponseFromCapture } from '../RfiResponse/hooks';
import { AiAssistBar, AiAssistButton } from './AiAssist';
import { CaptureCompanyLinksPanel } from './CaptureCompanyLinksPanel';
import { CaptureProfilePanel } from './CaptureProfilePanel';
import { CaptureTasksPanel } from './CaptureTasksPanel';
import { CaptureThemesPanel } from './CaptureThemesPanel';
import { CHANNELS, formatTs, labelOf, RFI_TYPES, SENTIMENTS, tsMs, WORKSTREAM_STATUSES, WORKSTREAMS, workstreamLabel } from './constants';
import { DocumentsPanel } from './DocumentsPanel';
import { InteractionSheet, WorkstreamSheet } from './entitySheets';
import { GoNoGoPanel } from './GoNoGoPanel';
import { GraphPanel } from './GraphPanel';
import { HypothesisCard } from './HypothesisCard';
import { useCaptureWorkspace } from './hooks';
import { useCustomerThemes } from './themeHooks';
const SENTIMENT_STYLE = {
    positive: 'bg-green-50 text-green-700',
    neutral: 'bg-gray-100 text-gray-600',
    negative: 'bg-red-50 text-red-700',
    unknown: 'bg-gray-50 text-gray-500'
};
function SentimentBadge({ value }) {
    if (!value) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    return /*#__PURE__*/ _jsx("span", {
        className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${SENTIMENT_STYLE[value] ?? SENTIMENT_STYLE.unknown}`,
        children: labelOf(SENTIMENTS, value)
    });
}
const RFI_COLUMNS = [
    {
        key: 'title',
        header: 'Title',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'rfiType',
        header: 'Type',
        sortable: true,
        filter: 'select',
        filterOptions: RFI_TYPES,
        render: (r)=>labelOf(RFI_TYPES, r.rfiType)
    },
    {
        key: 'eventDate',
        header: 'Event',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.eventDate),
        render: (r)=>formatTs(r.eventDate)
    },
    {
        key: 'responseDue',
        header: 'Response due',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.responseDue),
        render: (r)=>formatTs(r.responseDue)
    }
];
const INTERACTION_COLUMNS = [
    {
        key: 'summary',
        header: 'Summary',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'channel',
        header: 'Channel',
        sortable: true,
        filter: 'select',
        filterOptions: CHANNELS,
        render: (r)=>labelOf(CHANNELS, r.channel)
    },
    {
        key: 'stakeholder',
        header: 'Stakeholder',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'sentiment',
        header: 'Sentiment',
        filter: 'select',
        filterOptions: SENTIMENTS,
        render: (r)=>/*#__PURE__*/ _jsx(SentimentBadge, {
                value: r.sentiment
            })
    },
    {
        key: 'interactionDate',
        header: 'Date',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.interactionDate),
        render: (r)=>formatTs(r.interactionDate)
    }
];
const WORKSTREAM_COLUMNS = [
    {
        key: 'title',
        header: 'Title',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'workstreamType',
        header: 'Workstream',
        sortable: true,
        filter: 'select',
        filterOptions: WORKSTREAMS.map((w)=>({
                value: String(w.value),
                label: w.label
            })),
        render: (r)=>workstreamLabel(r.workstreamType)
    },
    {
        key: 'status',
        header: 'Status',
        sortable: true,
        filter: 'select',
        filterOptions: WORKSTREAM_STATUSES,
        render: (r)=>labelOf(WORKSTREAM_STATUSES, r.status)
    },
    {
        key: 'aiDrafted',
        header: 'AI',
        render: (r)=>r.aiDrafted ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                children: "AI"
            }) : null
    }
];
export function CaptureDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const captureProjectId = id ?? '';
    const { data, isLoading, isError, error } = useCaptureWorkspace(captureProjectId);
    const { data: rfiLinkData } = useListRfiResponsesForCapture(captureProjectId);
    const { data: customerThemes } = useCustomerThemes(captureProjectId);
    const createRfiResponse = useCreateRfiResponseProject();
    const unlinkRfiResponse = useUnlinkRfiResponseFromCapture();
    // One workspace correlation per page mount — groups the session's audit rows.
    const workspaceCorrelationId = useMemo(()=>crypto.randomUUID(), []);
    const rfiPrefs = useTablePreferences('ts:capture:rfis');
    const interactionPrefs = useTablePreferences('ts:capture:interactions');
    const workstreamPrefs = useTablePreferences('ts:capture:workstreams');
    const [interactionSheet, setInteractionSheet] = useState({
        open: false,
        value: null
    });
    const [workstreamSheet, setWorkstreamSheet] = useState({
        open: false,
        value: null
    });
    const [convertOpen, setConvertOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 px-6 py-4",
                "data-testid": Testids.deliveryShared.loadingSkeleton,
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-8 w-1/3"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-3 gap-3",
                        children: [
                            0,
                            1,
                            2
                        ].map((i)=>/*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-40"
                            }, i))
                    })
                ]
            })
        });
    }
    if (isError || !data?.summary) {
        // A missing capture project gets a human not-found with a way out —
        // never the raw backend error chain ("[not_found] get capture_project
        // …: record not found").
        const notFound = error instanceof ConnectError && error.code === Code.NotFound;
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4",
                children: notFound || !error ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: SearchX,
                    title: "Pursuit not found",
                    description: "It may have been deleted, or you may not have access to it.",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: ()=>navigate('/capture'),
                        children: "Back to Pursuits"
                    })
                }) : /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Pursuit workspace unavailable",
                    error: error
                })
            })
        });
    }
    const summary = data.summary;
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 px-6 py-4",
                "data-testid": Testids.capture.workspaceRoot,
                "data-capture-id": summary.captureProjectId,
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        className: "flex items-start justify-between gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h1", {
                                        className: "text-2xl font-semibold",
                                        children: summary.name
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mt-1 text-sm text-muted-foreground",
                                        children: [
                                            "Pursuit •",
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700",
                                                children: summary.stage
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(LinkedChips, {
                                        className: "mt-2",
                                        kind: LifecycleNodeKind.PURSUIT,
                                        id: summary.captureProjectId
                                    })
                                ]
                            }),
                            summary.stage === 'converted' ? /*#__PURE__*/ _jsx("span", {
                                className: "shrink-0 text-sm text-muted-foreground",
                                children: "Converted to proposal"
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                className: "shrink-0",
                                onClick: ()=>setConvertOpen(true),
                                "data-testid": Testids.capture.convertButton,
                                children: "Convert to Proposal"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ConvertToProposalDialog, {
                        open: convertOpen,
                        onOpenChange: setConvertOpen,
                        captureProjectId: summary.captureProjectId
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-full",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraTabBar, {
                                idBase: "capture-detail",
                                value: activeTab,
                                onValueChange: setActiveTab,
                                options: [
                                    {
                                        value: 'overview',
                                        label: 'Overview',
                                        testId: Testids.capture.tabOverview
                                    },
                                    {
                                        value: 'rfis',
                                        label: `RFIs (${(rfiLinkData?.workspaces.length ?? 0) + (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length)})`,
                                        testId: Testids.capture.tabRfis
                                    },
                                    {
                                        value: 'interactions',
                                        label: `Interactions (${data.interactions.length})`,
                                        testId: Testids.capture.tabInteractions
                                    },
                                    {
                                        value: 'themes',
                                        label: `Customer Themes (${customerThemes?.length ?? 0})`,
                                        testId: Testids.capture.tabThemes
                                    },
                                    {
                                        value: 'workstreams',
                                        label: `Workstreams (${data.workstreamItems.length})`,
                                        testId: Testids.capture.tabWorkstreams
                                    },
                                    {
                                        value: 'documents',
                                        label: 'Documents',
                                        testId: Testids.capture.tabDocuments
                                    }
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'overview',
                                idBase: "capture-detail",
                                value: "overview",
                                className: "mt-2 space-y-4",
                                children: [
                                    /*#__PURE__*/ _jsx(HypothesisCard, {
                                        captureProjectId: captureProjectId,
                                        summary: summary
                                    }),
                                    /*#__PURE__*/ _jsx(GoNoGoPanel, {
                                        captureProjectId: captureProjectId,
                                        decisions: data.goNoGoDecisions ?? [],
                                        correlationId: workspaceCorrelationId
                                    }),
                                    /*#__PURE__*/ _jsx(CaptureProfilePanel, {
                                        captureProjectId: captureProjectId,
                                        pursuitName: summary.name,
                                        profile: data.profile,
                                        competitionLinks: data.competitionLinks,
                                        correlationId: workspaceCorrelationId
                                    }),
                                    /*#__PURE__*/ _jsx(CaptureCompanyLinksPanel, {
                                        captureProjectId: captureProjectId,
                                        teamingLinks: data.teamingLinks,
                                        competitionLinks: data.competitionLinks,
                                        correlationId: workspaceCorrelationId
                                    }),
                                    /*#__PURE__*/ _jsx(CaptureTasksPanel, {
                                        captureProjectId: captureProjectId,
                                        tasks: data.tasks ?? [],
                                        correlationId: workspaceCorrelationId
                                    }),
                                    /*#__PURE__*/ _jsx(AiAssistBar, {
                                        zouProjectId: summary.zouProjectId
                                    }),
                                    /*#__PURE__*/ _jsx(GraphPanel, {
                                        highlightRef: summary.customerOrgId
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'rfis',
                                idBase: "capture-detail",
                                value: "rfis",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            "data-testid": Testids.capture.createRfiResponseButton,
                                            onClick: ()=>{
                                                const title = summary.name ? `RFI — ${summary.name}` : 'RFI Response';
                                                createRfiResponse.mutate({
                                                    title,
                                                    captureProjectId
                                                }, {
                                                    onSuccess: (s)=>{
                                                        navigate(`/rfi-response/${s.zouProjectId || s.rfiResponseProjectId}`);
                                                    }
                                                });
                                            },
                                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                className: "h-4 w-4"
                                            }),
                                            disabled: createRfiResponse.isPending,
                                            children: "Create RFI Response"
                                        })
                                    }),
                                    (rfiLinkData?.workspaces.length ?? 0) > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-sm font-medium text-muted-foreground",
                                                children: "Response workspaces"
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                className: "divide-y rounded-md border",
                                                children: (rfiLinkData?.workspaces ?? []).map((w)=>/*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center justify-between px-3 py-2 hover:bg-muted/50",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("button", {
                                                                    type: "button",
                                                                    className: "flex min-w-0 flex-1 items-center justify-between text-left text-sm",
                                                                    "data-testid": Testids.capture.rfiWorkspaceRow,
                                                                    onClick: ()=>navigate(`/rfi-response/${w.zouProjectId || w.rfiResponseProjectId}`),
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "truncate font-medium",
                                                                            children: w.title
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "ml-2 shrink-0 text-muted-foreground",
                                                                            children: w.status
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                                    size: "sm",
                                                                    variant: "ghost",
                                                                    className: "shrink-0",
                                                                    "data-testid": Testids.capture.unlinkRfiWorkspaceButton,
                                                                    disabled: unlinkRfiResponse.isPending,
                                                                    onClick: ()=>unlinkRfiResponse.mutate({
                                                                            captureProjectId,
                                                                            rfiResponseProjectId: w.rfiResponseProjectId
                                                                        }),
                                                                    iconLeft: /*#__PURE__*/ _jsx(Unlink, {
                                                                        className: "h-4 w-4"
                                                                    }),
                                                                    "aria-label": "Unlink RFI Response workspace"
                                                                })
                                                            ]
                                                        })
                                                    }, w.rfiResponseProjectId))
                                            })
                                        ]
                                    }),
                                    (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length) > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-sm font-medium text-muted-foreground",
                                                children: "Legacy timeline events"
                                            }),
                                            /*#__PURE__*/ _jsx(EntityTable, {
                                                data: rfiLinkData?.legacyRfiEvents ?? data.rfiEvents,
                                                columns: [
                                                    ...RFI_COLUMNS,
                                                    {
                                                        key: 'legacy',
                                                        header: '',
                                                        render: ()=>/*#__PURE__*/ _jsx(SpecraBadge, {
                                                                variant: "neutral",
                                                                "data-testid": Testids.capture.rfiLegacyBadge,
                                                                children: "legacy"
                                                            })
                                                    }
                                                ],
                                                testid: Testids.capture.rfiTable,
                                                rowTestid: Testids.capture.rfiRow,
                                                rowKey: (r)=>r.id,
                                                sort: rfiPrefs.sort,
                                                onToggleSort: rfiPrefs.toggleSort,
                                                filters: rfiPrefs.filters,
                                                onFilterChange: rfiPrefs.setFilter,
                                                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                                    title: "No legacy RFIs",
                                                    description: "Older side-sheet RFI events appear here for reference."
                                                })
                                            })
                                        ]
                                    }),
                                    (rfiLinkData?.workspaces.length ?? 0) === 0 && (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length) === 0 && /*#__PURE__*/ _jsx(EmptyState, {
                                        title: "No RFI Responses yet",
                                        description: "Create a collaborative workspace to draft your answer — not a side sheet on this pursuit."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'interactions',
                                idBase: "capture-detail",
                                value: "interactions",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(AiAssistButton, {
                                                zouProjectId: summary.zouProjectId,
                                                action: "summarize-interactions",
                                                label: "Summarize",
                                                prompt: "Summarize the customer interactions logged on this capture. Call out positive signals, risks, and the follow-ups we owe."
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                "data-testid": Testids.capture.addInteractionButton,
                                                onClick: ()=>setInteractionSheet({
                                                        open: true,
                                                        value: null
                                                    }),
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "Log interaction"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(EntityTable, {
                                        data: data.interactions,
                                        columns: INTERACTION_COLUMNS,
                                        testid: Testids.capture.interactionTable,
                                        rowTestid: Testids.capture.interactionRow,
                                        rowKey: (r)=>r.id,
                                        onRowClick: (r)=>setInteractionSheet({
                                                open: true,
                                                value: r
                                            }),
                                        sort: interactionPrefs.sort,
                                        onToggleSort: interactionPrefs.toggleSort,
                                        filters: interactionPrefs.filters,
                                        onFilterChange: interactionPrefs.setFilter,
                                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                            title: "No interactions logged",
                                            description: "Log customer-shaping touchpoints — meetings, calls, emails, conferences."
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                active: activeTab === 'themes',
                                idBase: "capture-detail",
                                value: "themes",
                                className: "mt-2 space-y-3",
                                children: /*#__PURE__*/ _jsx(CaptureThemesPanel, {
                                    captureProjectId: captureProjectId,
                                    correlationId: workspaceCorrelationId
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'workstreams',
                                idBase: "capture-detail",
                                value: "workstreams",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(AiAssistButton, {
                                                zouProjectId: summary.zouProjectId,
                                                action: "draft-workstream",
                                                label: "Draft analysis",
                                                prompt: "Help me make progress on this capture's workstreams. Suggest concrete items for customer shaping, solutioning, black hat, and teaming, prioritized for where this pursuit is now."
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                "data-testid": Testids.capture.addWorkstreamItemButton,
                                                onClick: ()=>setWorkstreamSheet({
                                                        open: true,
                                                        value: null
                                                    }),
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "New item"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(EntityTable, {
                                        data: data.workstreamItems,
                                        columns: WORKSTREAM_COLUMNS,
                                        testid: Testids.capture.workstreamTable,
                                        rowTestid: Testids.capture.workstreamItemRow,
                                        rowKey: (r)=>r.id,
                                        onRowClick: (r)=>setWorkstreamSheet({
                                                open: true,
                                                value: r
                                            }),
                                        sort: workstreamPrefs.sort,
                                        onToggleSort: workstreamPrefs.toggleSort,
                                        filters: workstreamPrefs.filters,
                                        onFilterChange: workstreamPrefs.setFilter,
                                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                            title: "No workstream items",
                                            description: "Capture structured work across the 8 capture workstreams."
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                active: activeTab === 'documents',
                                idBase: "capture-detail",
                                value: "documents",
                                className: "mt-2 space-y-3",
                                children: /*#__PURE__*/ _jsx(DocumentsPanel, {
                                    entityType: "capture_project",
                                    entityId: captureProjectId,
                                    title: "Pursuit documents"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InteractionSheet, {
                open: interactionSheet.open,
                onOpenChange: (open)=>setInteractionSheet((s)=>({
                            ...s,
                            open
                        })),
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                interaction: interactionSheet.value
            }),
            /*#__PURE__*/ _jsx(WorkstreamSheet, {
                open: workstreamSheet.open,
                onOpenChange: (open)=>setWorkstreamSheet((s)=>({
                            ...s,
                            open
                        })),
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                item: workstreamSheet.value,
                workstreamType: ProjectDataTypes.CaptureWorkstreamType.CUSTOMER_SHAPING
            })
        ]
    });
}
